import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SalesForecast {
  forecast_month: string;
  predicted_revenue: number;
  predicted_bookings: number;
  confidence_level: number;
}

export const useSalesForecast = (monthsAhead: number = 6) => {
  return useQuery({
    queryKey: ["sales-forecast", monthsAhead],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('calculate_sales_forecast', { months_ahead: monthsAhead });
      
      if (error) throw error;
      return data as SalesForecast[];
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  });
};

export interface SalesTrend {
  period_label: string;
  revenue: number;
  bookings: number;
  growth_rate: number;
  trend_direction: string;
}

export const useSalesTrends = (periodType: string = 'month', periodsBack: number = 12) => {
  return useQuery({
    queryKey: ["sales-trends", periodType, periodsBack],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('analyze_sales_trends', { 
          period_type: periodType,
          periods_back: periodsBack 
        });
      
      if (error) throw error;
      return data as SalesTrend[];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

export interface SeasonalityData {
  month: number;
  month_name: string;
  seasonal_factor: number;
  historical_average: number;
  peak_season: boolean;
}

export const useSeasonality = () => {
  return useQuery({
    queryKey: ["sales-seasonality"],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('calculate_seasonality');
      
      if (error) throw error;
      return data as SeasonalityData[];
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });
};
